import React, { useState } from 'react';
import { BookOpen, CaretRight } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { MEMBER_DOCS, findMemberDoc } from '../content/memberDocs';

/**
 * The Society's own documents — read here, and from the invitation.
 *
 * They live in the Codex rather than in the Vault, and the reason is worth
 * keeping in view: a member who has forgotten their vault passphrase could not
 * open the document that explains what to do about it. The Vault holds what is
 * yours; this holds what is the Society's.
 *
 * One reader serves both surfaces. On the invitation it opens over the
 * parchment in the app's own dark, which reads as a separate paper being handed
 * across rather than as part of the letter — which is what it is.
 */

const DocReader = ({ doc, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-w-lg max-h-[85dvh] overflow-y-auto"
      data-testid={`doc-reader-${doc?.id || 'none'}`}
    >
      {doc && (
        <>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{doc.title}</DialogTitle>
            <DialogDescription>{doc.summary}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {doc.sections.map((s) => (
              <section key={s.heading} className="space-y-2">
                <h3
                  className="font-heading text-base leading-snug"
                  style={{ color: 'hsl(var(--gold))' }}
                >
                  {s.heading}
                </h3>
                {s.body.map((para, i) => (
                  <p
                    key={i}
                    className={`text-sm leading-relaxed ${i === 0 ? 'text-foreground' : 'text-muted-foreground'}`}
                    style={{ overflowWrap: 'anywhere' }}
                  >
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

/** The list, for the Codex. */
export const MemberDocuments = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="member-documents">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
        <h2 className="font-heading text-lg">The Society's documents</h2>
      </div>

      <p className="text-sm text-muted-foreground">
        Kept here so they can be read at the moment you need them, rather than
        only at the moment you agreed to them.
      </p>

      <div className="divide-y divide-border border-t border-border">
        {MEMBER_DOCS.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setOpenId(d.id)}
            className="w-full flex items-center gap-3 py-3 text-left hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            data-testid={`doc-open-${d.id}`}
          >
            {/* min-w-0 so a long summary shrinks instead of pushing the
                chevron off a phone screen. */}
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-foreground">{d.title}</span>
              <span className="block text-xs text-muted-foreground">{d.summary}</span>
            </span>
            <CaretRight className="w-4 h-4 shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>

      <DocReader
        doc={findMemberDoc(openId)}
        open={!!openId}
        onOpenChange={(o) => { if (!o) setOpenId(null); }}
      />
    </div>
  );
};

/** A link to one document, for surfaces that are not the Codex. */
export const MemberDocumentLink = ({ id, className, style, children }) => {
  const [open, setOpen] = useState(false);
  const doc = findMemberDoc(id);
  if (!doc) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className}
        style={style}
        data-testid={`doc-link-${id}`}
      >
        {children || doc.title}
      </button>
      <DocReader doc={doc} open={open} onOpenChange={setOpen} />
    </>
  );
};

export default MemberDocuments;
